import React, { useEffect, useState } from 'react';

import { hasRoles, roleDisplayValueMap, EDIT_STUDY_ROLES } from '../../../../util/RoleUtil';

import { SaveButton } from '../../../common/button/save-button/SaveButton';
import { Table } from '../../../common/table/Table';
import Modal from '../../../common/modal/Modal';
import { StaffEditAssignments } from './StaffEditAssignments';

export const StudyTrialEditStaffTab = ({ context, setContext, navigateTo, study, handleUpdate }) => {
  const [studyName, setStudyName] = useState('');
  const [assignedStaff, setAssignedStaff] = useState([]);
  const [availableStaff, setAvailableStaff] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const fieldsDisabled = !hasRoles(context, EDIT_STUDY_ROLES);

  useEffect(() => {
    if (study) {
      setStudyName(study?.protocolID + ' ' + study?.studyName + ' ' + study?.facility);
      setAvailableStaff(study.availableSiteStaff);

      const allStaff = study.availableSiteStaff.flatMap((role) => {
        return role.viewSiteStaffVOS
          .filter((user) => user.selected)
          .map((user) => ({
            ...user,
            studyRole: role.roleName,
          }));
      });
      setAssignedStaff(allStaff);
    }
  }, [study]);

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const handleAddStaffClick = () => {
    setIsModalOpen(true);
  };

  const handleSaveChangesClick = () => {
    const roles = assignedStaff.reduce((acc, user) => {
      const { studyRole, personExternalReference } = user;
      if (!acc[studyRole]) {
        acc[studyRole] = [];
      }
      acc[studyRole].push(personExternalReference);
      return acc;
    }, {});

    const rolesArray = Object.entries(roles).map(([roleName, personExternalReferences]) => ({
      roleName,
      personExternalReferences,
    }));

    const updateStudyStudyScheduleVOS = (study?.visitSchedules || []).map((schedule) => ({
      name: schedule.name || '',
      studyScheduleExternalReference: schedule.studyScheduleExternalReference,
      updateStudyVisitScheduleVOS: (schedule.visitPacks || []).map((visit) => ({
        enrolmentVisit: !!visit.enrolmentVisit,
        day: Number(visit.day) || 0,

        formExternalReferences: (visit.forms || []).map((form) => form.formExternalReference),

        longVisit: !!visit.longVisit,
        visitCode: visit.visitCode,

        ...(visit.visitCodeEdited && visit.visitCodeToUpdate ? { visitCodeToUpdate: visit.visitCodeToUpdate } : {}),

        visitType: visit.visitType,
        windowAfter: Number(visit.windowAfter) || 0,
        windowBefore: Number(visit.windowBefore) || 0,
      })),
    }));

    const newStudy = {
      ...study,
      facilityExternalReferenceToUpdate: study.facilityExternalReference,
      roles: rolesArray,
      updateStudyStudyScheduleVOS,
    };
    handleUpdate(newStudy);
  };

  const handleCancelClick = () => {
    setContext((prevState) => ({
      ...prevState,
      viewMode: false,
      breadcrumbs: {
        ...prevState.breadcrumbs,
        newStudySitesBreadcrumb: undefined,
        newStudyGeneralBreadcrumb: undefined,
        newStudyFormsBreadcrumb: undefined,
      },
      editMode: true,
    }));
    navigateTo('studyTrialAdmin');
  };

  const mapStaffMembers = () => {
    return assignedStaff.map((staffMember) => ({
      ...staffMember,
      studyRole: roleDisplayValueMap.get(staffMember.studyRole) ?? staffMember.studyRole,
    }));
  };

  return (
    <div className="study-trials-staff-tab-container">
      <div className="study-trials-staff-edit-container">
        <h3>{studyName}</h3>
        <Table
          tableData={mapStaffMembers()}
          hiddenColumns={['personExternalReference', 'systemUserExternalReference', 'selected', 'name']}
          searchEnabled={true}
          filterEnabled={true}
          onRowClick={null}
          tableTitle={'Staff'}
          buttonText={!fieldsDisabled ? 'Add/Edit' : undefined}
          onButtonClick={handleAddStaffClick}
          buttonOneWidth={'135px'}
          buttonDisabled={false}
          maxRowCount={5}
          horizontalScroll
        />
        {isModalOpen && (
          <Modal
            closeModal={closeModal}
            setIsModalOpen={setIsModalOpen}
            headerTitle={studyName}
            width={'950px'}
            height={'max-content'}
            contentPadding={'10px'}
            justifyContent={'center'}
            hideButton={true}
          >
            <StaffEditAssignments
              closeModal={closeModal}
              assignedStaff={assignedStaff}
              setAssignedStaff={setAssignedStaff}
              availableStaff={availableStaff}
            />
          </Modal>
        )}
      </div>
      <div className="study-button-container">
        <SaveButton
          color={'#ffffff'}
          border={'1px solid #db602e'}
          textColor={'#db602e'}
          title={'Cancel'}
          onClick={handleCancelClick}
          disabled={fieldsDisabled}
        />
        <SaveButton
          color={'#db602e'}
          title={'Save Changes'}
          onClick={handleSaveChangesClick}
          disabled={fieldsDisabled}
        />
      </div>
    </div>
  );
};
